import type { settingTypes } from '@ipfabric/utils';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { hasHttpOrHttps } from 'utils/hasHttpOrHtttps';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlField } from './fields/FormikVendorApiBaseUrlField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { FormikVendorApiUsernamePasswordFields } from './fields/FormikVendorApiUsernamePasswordFields';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

export type RuckusFormValues =
  BaseVendorFormValues<settingTypes.RuckusVszApiSettings>;

type Props = VendorDialogProps<
  RuckusFormValues,
  settingTypes.RuckusVszApiSettings,
  settingTypes.UpdateRuckusVszApiSettings
>;

const resetSecretAttributes = (values: RuckusFormValues): RuckusFormValues => {
  return {
    ...values,
    username: '',
    password: '',
  };
};

const BareRuckusForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const formik = useFormik<RuckusFormValues>({
    initialValues: resetSecretAttributes(props.initialValues),
    validate: (values) => {
      const { required, baseUrlStartsWith } =
        msg.discoverySettings.vendorsApi.validations;
      const errors: FormikErrors<RuckusFormValues> = {};

      if (!values.baseUrl) {
        errors.baseUrl = required;
      } else if (!hasHttpOrHttps(values.baseUrl)) {
        errors.baseUrl = baseUrlStartsWith;
      }
      if (!editMode && !values.username) {
        errors.username = required;
      }
      if (!editMode && !values.password) {
        errors.password = required;
      }
      if (!values.apiVersion) {
        errors.apiVersion = required;
      }
      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit((values: RuckusFormValues) => {
      const formattedValues = {
        ...values,
        ...getAdvancedSettingsDefaultValues(values),
      };

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...formattedValues,
          id: values.id,
          username:
            values.username || props.initialValues.username || undefined,
          password:
            values.password || props.initialValues.password || undefined,
        });
      } else if (props.dialogType === 'add') {
        props.onSubmit(formattedValues);
      }
    }),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<RuckusFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikVendorApiUsernamePasswordFields
        formik={formik}
        isEditMode={editMode}
      />
      <FormikSelectField
        formik={formik}
        label={msg.discoverySettings.vendorsApi.labels.apiVersion}
        name='apiVersion'
        nonClearable
        options={[{ label: 'v9.1', value: 'v9_1' }]}
      />
      <FormikVendorApiBaseUrlField formik={formik} />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const RuckusForm = memo(BareRuckusForm);
